import { TIPO_LABELS } from '../constants/processoMeta';
import type {
  AndamentoProcesso,
  CitandoProcesso,
  DocumentoProcesso,
  ProcessoArbitro,
  ProcessoArbitroSeed,
  ResumoCitacao,
} from '../types';
import { adicionarDias } from './date';

const iniciaisFrase = (nome: string) => nome.split(/\s+/).slice(0, 2).join(' ');

export const montarAndamentos = (processo: ProcessoArbitroSeed): AndamentoProcesso[] => {
  const eventos: Omit<AndamentoProcesso, 'id'>[] = [];

  eventos.push({
    data: adicionarDias(processo.atribuidoEm, -1),
    hora: '11:00',
    titulo: 'Certidão de triagem',
    descricao: 'Processo triado e distribuído à Câmara de Arbitragem.',
    autor: 'Secretaria · Núbia Rangel',
    tipo: 'documento',
  });
  eventos.push({
    data: processo.atribuidoEm,
    hora: '08:30',
    titulo: 'Nomeação do árbitro',
    descricao: 'Você foi nomeado(a) árbitro neste processo pela Câmara.',
    autor: 'Secretaria · Núbia Rangel',
    tipo: 'sistema',
  });

  if (processo.termoImparcialidade) {
    eventos.push({
      data: adicionarDias(processo.atribuidoEm, 2),
      hora: '09:10',
      titulo: 'Termo de Imparcialidade publicado',
      descricao: 'Você declarou ausência de impedimento e se constituiu no processo.',
      autor: 'Você · Dr. Ricardo Almeida',
      tipo: 'termo',
    });
  }
  if (processo.termoArbitragem) {
    eventos.push({
      data: adicionarDias(processo.atribuidoEm, 2),
      hora: '09:11',
      titulo: 'Termo de Arbitragem publicado',
      descricao: 'Termo de Arbitragem emitido pela secretaria. Processo apto a prosseguir.',
      autor: 'Secretaria · Núbia Rangel',
      tipo: 'termo',
    });
  }

  if (processo.situacao === 'concluso_recebido') {
    eventos.push({
      data: processo.ultimaMovimentacao,
      hora: '15:10',
      titulo: 'Concluso encaminhado',
      descricao: 'Secretaria encaminhou manifestação da parte requerida para sua análise.',
      autor: 'Secretaria · Núbia Rangel',
      tipo: 'concluso',
      anexos: ['Manifestação da parte requerida.pdf'],
    });
  } else if (processo.situacao === 'aguardando_despacho') {
    eventos.push({
      data: processo.ultimaMovimentacao,
      hora: '10:00',
      titulo: 'Autos conclusos para despacho',
      descricao: 'Processo aguardando despacho do árbitro.',
      autor: 'Sistema',
      tipo: 'sistema',
    });
  } else if (processo.situacao === 'decurso_prazo' || processo.situacao === 'aguardando_sentenca') {
    eventos.push({
      data: processo.ultimaMovimentacao,
      hora: '17:00',
      titulo: 'Decurso de prazo',
      descricao: 'Transcorreu o prazo sem manifestação. Processo concluso para sentença.',
      autor: 'Sistema',
      tipo: 'sistema',
    });
  } else if (processo.situacao === 'embargos_opostos') {
    eventos.push({
      data: processo.ultimaMovimentacao,
      hora: '11:20',
      titulo: 'Embargos de declaração',
      descricao: 'Parte requerida opôs embargos de declaração — pendentes de resposta.',
      autor: 'Parte requerida',
      tipo: 'concluso',
    });
  }

  if (processo.dataSentenca) {
    eventos.push({
      data: processo.dataSentenca,
      hora: '14:00',
      titulo: 'Sentença publicada',
      descricao: 'Sentença arbitral publicada. Prazo de embargos iniciado.',
      autor: 'Você · Dr. Ricardo Almeida',
      tipo: 'sentenca',
    });
  }

  if (processo.status === 'concluido') {
    eventos.push({
      data: processo.ultimaMovimentacao,
      hora: '16:00',
      titulo: 'Processo encerrado',
      descricao: 'Trânsito em julgado certificado. Processo encerrado.',
      autor: 'Você · Dr. Ricardo Almeida',
      tipo: 'encerramento',
    });
  }

  return eventos
    .sort((primeiro, segundo) =>
      `${primeiro.data} ${primeiro.hora}`.localeCompare(`${segundo.data} ${segundo.hora}`),
    )
    .reverse()
    .map((evento, indice) => ({ ...evento, id: `and-${processo.id}-${indice}` }));
};

export const montarDocumentos = (processo: ProcessoArbitroSeed): DocumentoProcesso[] => {
  const documentos: Omit<DocumentoProcesso, 'id'>[] = [
    {
      nome: `Petição inicial — ${iniciaisFrase(processo.requerente.nome)}.pdf`,
      data: processo.atribuidoEm,
      autor: processo.requerente.nome,
      kind: 'peticao',
    },
    {
      nome: `Contrato ${processo.contrato.numero}.pdf`,
      data: processo.atribuidoEm,
      autor: 'Secretaria',
      kind: 'contrato',
    },
    {
      nome: 'Certidão de triagem.pdf',
      data: adicionarDias(processo.atribuidoEm, -1),
      autor: 'Secretaria',
      kind: 'ato',
    },
  ];

  if (processo.termoImparcialidade) {
    documentos.push({
      nome: 'Termo de Imparcialidade.pdf',
      data: adicionarDias(processo.atribuidoEm, 2),
      autor: 'Você',
      kind: 'termo',
    });
  }
  if (processo.termoArbitragem) {
    documentos.push({
      nome: 'Termo de Arbitragem.pdf',
      data: adicionarDias(processo.atribuidoEm, 2),
      autor: 'Secretaria',
      kind: 'termo',
    });
  }

  processo.pendencias.forEach((pendencia) => {
    if (pendencia.tipo === 'concluso') {
      documentos.push({
        nome: 'Manifestação da parte requerida.pdf',
        data: processo.ultimaMovimentacao,
        autor: 'Parte requerida',
        kind: 'parte',
      });
    }
    if (pendencia.tipo === 'embargos') {
      documentos.push({
        nome: 'Embargos de declaração — requerida.pdf',
        data: processo.ultimaMovimentacao,
        autor: 'Parte requerida',
        kind: 'parte',
      });
    }
  });

  if (processo.dataSentenca) {
    documentos.push({
      nome: 'Sentença arbitral.pdf',
      data: processo.dataSentenca,
      autor: 'Você',
      kind: 'ato',
    });
  }
  if (processo.status === 'concluido') {
    documentos.push({
      nome: 'Certidão de trânsito em julgado.pdf',
      data: processo.ultimaMovimentacao,
      autor: 'Sistema',
      kind: 'ato',
    });
  }

  return documentos.map((documento, indice) => ({
    ...documento,
    id: `doc-${processo.id}-${indice}`,
  }));
};

// Mock: número de requeridos (a partir do fim da lista) ainda sem citação confirmada,
// para demonstrar o bloqueio de publicação de sentença por citação parcial.
const CITACAO_PENDENTES_MOCK: Record<string, number> = {
  p5: 2,
};

export const montarCitandos = (processo: ProcessoArbitroSeed): CitandoProcesso[] => {
  const requeridos = processo.requeridos ?? [processo.requerido];
  const emCitacao = processo.status === 'em_citacao';
  const pendentesForcados = CITACAO_PENDENTES_MOCK[processo.id] ?? 0;
  const total = requeridos.length;

  return requeridos.map((parte, indice) => {
    const forcarPendente = indice >= total - pendentesForcados;
    const citado = !emCitacao && !forcarPendente;
    return {
      nome: parte.nome,
      documento: parte.documento,
      papel: 'Requerido',
      via: 'ar',
      status: citado ? 'citado' : 'pendente',
      data: citado ? adicionarDias(processo.atribuidoEm, 6) : null,
      bloqueiaSentenca: !citado,
      citacao: null,
      motivoEncerramentoFisico: null,
    };
  });
};

export const resumoCitacao = (citandos: CitandoProcesso[]): ResumoCitacao => {
  const total = citandos.length;
  const citados = citandos.filter((citando) => citando.status === 'citado').length;
  const estado = citados === 0 ? 'nenhuma' : citados === total ? 'completa' : 'parcial';
  return { total, citados, pendentes: total - citados, estado };
};

export const montarRelato = (processo: ProcessoArbitroSeed): string[] => {
  const tipoTexto: Record<string, string> = {
    rescisao: `postula a rescisão do contrato ${processo.contrato.numero}, em razão do inadimplemento das obrigações pactuadas`,
    despejo_fp: 'requer o despejo do imóvel locado por falta de pagamento dos aluguéis e encargos',
    despejo_alug:
      'requer o despejo do imóvel cumulado com a cobrança dos aluguéis e encargos em atraso',
    cobranca: `visa ao recebimento dos valores em aberto decorrentes do contrato ${processo.contrato.numero}`,
  };
  return [
    `${processo.requerente.nome} instaurou o presente procedimento arbitral em face de ${processo.requerido.nome} e ${tipoTexto[processo.tipo]}.`,
    `O valor atribuído à causa é de ${processo.contrato.valor}, com fundamento na cláusula compromissória (${processo.contrato.clausulaArbitral}) prevista no contrato firmado entre as partes.`,
    `Regularmente instaurado o procedimento, os autos seguem sob a condução do árbitro para os atos subsequentes — ${TIPO_LABELS[processo.tipo]}.`,
  ];
};

export const enriquecerProcesso = (seed: ProcessoArbitroSeed): ProcessoArbitro => ({
  ...seed,
  andamentos: montarAndamentos(seed),
  documentos: montarDocumentos(seed),
  citandos: montarCitandos(seed),
});
